<template>
  <v-container>
    <v-card outlined>
      <v-card-title>
        {{ model.verboseNamePlural }}
        <router-link
          v-slot="{ href, navigate }"
          :to="{ name: `New${model.modelName}` }"
        >
          <v-btn
            v-if="profile.is_staff"
            depressed
            small
            color="primary"
            :href="href"
            class="ml-4"
            @click="navigate"
          >
            Aggiungi
            <v-icon right>fa-plus</v-icon>
          </v-btn>
        </router-link>
      </v-card-title>
      <v-container class="py-0">
        <v-row>
          <v-col v-for="filter in filters" :key="filter.name" sm="3">
            <v-checkbox
              v-if="filter.type === 'boolean'"
              v-model="enabledFilters[filter.name]"
              dense
              class="mt-1"
              :label="filter.label"
            ></v-checkbox>
            <autocomplete
              v-else
              v-model="enabledFilters[filter.name]"
              lazy
              dense
              clearable
              outlined
              :model="filter.model"
              :item-text="filter.text"
              item-value="id"
              :label="filter.model.verboseName"
            ></autocomplete>
          </v-col>

          <v-col justify-self="end">
            <v-text-field
              v-model="search"
              prepend-icon="fa-search"
              :label="$gettext('Cerca')"
              class="pt-1 mt-0"
              clearable
              single-line
              hide-details
              @input="delayedUpdate"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-container>
      <v-data-table
        :headers="headers"
        :items="items.results"
        :options.sync="options"
        :footer-props="{
          itemsPerPageOptions: [100, 200, 500, 10, 30, 50],
          showCurrentPage: true,
          showFirstLastPage: true,
        }"
        :server-items-length="items.count"
        :must-sort="true"
        :loading="loading"
        :elevation="0"
      >
        <template
          v-for="(_, slotName) in $scopedSlots"
          v-slot:[slotName]="context"
        >
          <slot :name="slotName" v-bind="context" />
        </template>

        <template v-slot:item.id="{ item }">
          <router-link
            v-slot="{ href, navigate }"
            :to="{ name: `Edit${model.modelName}`, params: { id: item.id } }"
          >
            <v-chip
              outlined
              small
              link
              color="primary"
              :href="href"
              @click="navigate"
            >
              {{ item.id }}
            </v-chip>
          </router-link>
        </template>
        <template v-slot:item.modified="{ item }">
          <time-ago :date-str="item.modified" />
        </template>
        <template v-slot:item.actions="{ item }">
          <router-link
            v-slot="{ href, navigate }"
            :to="{ name: `Edit${model.modelName}`, params: { id: item.id } }"
          >
            <v-icon link small class="mr-2" :href="href" @click="navigate">
              fa-edit
            </v-icon>
          </router-link>
          <v-icon v-if="!disableDelete" small @click="destroy(item.id)">
            fa-trash
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapState } from "vuex"
import { debounce, pickBy } from "@/lodash"

const defaultOptions = () => ({
  page: 1,
  itemsPerPage: 50,
  sortBy: ["id"],
  sortDesc: [true],
})

export default {
  props: {
    model: {
      type: Object,
      required: true,
    },
    modelHeaders: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Array,
      default: () => [],
    },
    disableDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      enabledFilters: this.filters.reduce((acc, f) => {
        acc[f.name] = undefined
        return acc
      }, {}),
      options: defaultOptions(),
      search: "",
    }
  },
  computed: {
    headers() {
      return [{ text: "ID", value: "id" }].concat(this.modelHeaders).concat([
        { text: "Modificato", value: "modified" },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "end",
        },
      ])
    },
    activeFilters() {
      return pickBy(this.enabledFilters)
    },
    ...mapState({
      loading(state) {
        return state[this.model.appName].pending[this.model.propNamePlural]
      },
      items(state) {
        return state[this.model.appName][this.model.propNamePlural]
      },
    }),
  },
  watch: {
    options: {
      handler() {
        this.fetch()
      },
      deep: true,
    },
    activeFilters: {
      handler() {
        this.fetch()
      },
      deep: true,
    },
  },
  mounted() {
    // PersonCategory --> PERSON_CATEGORY
    const capitalizedModel = this.model.propName
      .replace(/([A-Z])/g, "_$1")
      .toUpperCase()

    // we listen to a CREATE, UPDATE or DESTROY operation on the store for this
    // model. if anything happens, we pre-refetch the list so it's already
    // available when switching back to the list page
    this.$store.subscribe((mutation) => {
      if (
        mutation.type === `UPDATE_${capitalizedModel}_SUCCEEDED` ||
        mutation.type === `DESTROY_${capitalizedModel}_SUCCEEDED`
      ) {
        this.fetch()
      } else if (mutation.type === `CREATE_${capitalizedModel}_SUCCEEDED`) {
        // if the mutation is a CREATE, we want to reset the table options so
        // that the new element appears on top.
        // changing the options already invokes `fetch()`
        this.options = defaultOptions()
      }
    })
  },
  methods: {
    fetch() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      this.$store.dispatch(`list${this.model.modelNamePlural}`, {
        params: {
          ordering: (sortDesc[0] ? "-" : "") + sortBy[0],
          page_size: itemsPerPage,
          search: this.search,
          page,
          ...this.activeFilters,
        },
      })
    },
    async destroy(id) {
      let confirm = window.confirm(
        "Si è sicuri di voler cancellare questo elemento? L'azione è irreversibile."
      )
      if (confirm) {
        try {
          await this.$store.dispatch(`destroy${this.model.modelName}`, {
            params: { id },
          })
          this.$store.commit("core/setSnack", {
            level: "success",
            msg: "Eliminato",
          })
          this.fetch()
        } catch (error) {
          window.alert(error.response.data.error)
        }
      }
    },
    delayedUpdate: debounce(function () {
      this.options.page = 1
      this.fetch()
    }, 500),
  },
}
</script>
